import SearchIcon from '/public/images/search.svg';
import CloseIcon from '/public/images/close_green.svg';
import DotIcon from '/public/images/bg-dot.svg';
import Image from "next/image";
import {useEffect, useState} from "react";
import styled from "styled-components";
import {getColor} from "@/styles/ThemeConfig";

import ArrowUpActiveIcon from 'public/images/arrow_up_active.svg';
import NewIcon from 'public/images/new.svg';
import ArrowDownIcon from 'public/images/arrow_down.svg';
import ArrowDownActiveIcon from 'public/images/arrow_down_active.svg';
import LocationPinIcon from "../../../public/images/ic-pin.svg";
import Link from "next/link";
import SelectSearchLocation from "@/components/inputs/SelectSearchLocation";
import {useRouter} from "next/router";
import ForceSSRLink from "@/components/other/ForceSSRLink";

const LinkWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    flex-basis: ${props => props?.isWorldTour ? '70%' : '64%'};

    div.active {
        color: ${getColor('gold')} !important;
    }
`;

const LinkItem = styled.div`
    font-size: 14px;
    margin: 0;
    cursor: pointer;
    letter-spacing: 1px;
    color: ${getColor('greenText')};
    display: flex;
    align-items: center;
    gap: 5px;
    :hover {
        color: ${getColor('gold')};
    }
`;

const LinkItemLink = styled(ForceSSRLink)`
    font-size: 14px;
    margin: 0;
    cursor: pointer;
    letter-spacing: 1px;
    color: ${getColor('greenText')};
    display: flex;
    text-decoration: none;
    align-items: center;
    gap: 5px;
    :hover {
        color: ${getColor('gold')};
    }
`;

const ListContainer = styled.div`
    max-height: calc(100% - 50px);
    overflow-y: auto;
    overflow-x: hidden;
`;

const CountryList = styled.ul`
    padding:30px;
    column-count: 5;
    column-gap: 20px;
    list-style: none;
    //padding: 0;
    margin: 0;

    li {
        break-inside: avoid;
        margin-bottom: 10px;
        cursor: pointer;
    }

    @media (max-width: 768px) {
        column-count: 2;
    }

    @media (max-width: 480px) {
        column-count: 1;
    }
`;


const HeaderContainer = styled.div`
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
`;


const HeaderWrapper = styled.div`
    position: fixed;
    width: 100%;
    z-index: 1000;
    top: 0;
    transition: transform 0.5s ease-in-out; /* Smooth transition for sliding */
    transform: ${({ isScrollingUp }) => isScrollingUp ? 'translateY(0)' : 'translateY(-100%)'}; /* Slide out when scrolling down */
`;

const CountryLink = styled(ForceSSRLink)`
    text-decoration: none;
    all: unset;
    cursor: pointer;
    :hover {
        color:${getColor('gold')}
    }
    div{
        font-size: 18px;
    }
`

const CityLink = styled(ForceSSRLink)`
    text-decoration: none;
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 15px;
    color: ${getColor('greenText')};
    :hover {
        color:${getColor('gold')}
    }
`

export default function Header({ regions, headerData, searchOptions, isWorldTour = false }) {
    const [showRegions, setShowRegions] = useState(false);
    const [showRegionData, setShowRegionData] = useState(false);
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [isRegionsHover, setIsRegionsHover] = useState(false);
    const [isScrollingUp, setIsScrollingUp] = useState(true);

    const closeAll = () => {
        setShowRegions(false);
        setShowRegionData(false);
        setShowSearchBar(false);
    };

    const router = useRouter();
    useEffect(() => {
        const handleRouteChange = () => {
            closeAll();
        };

        // Listen to route changes
        router.events.on('routeChangeStart', handleRouteChange);

        // Cleanup the event listener on component unmount
        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router]);


    // Detect scroll direction
    useEffect(() => {
        // Prevent this effect from running during SSR/ISR
        if (typeof window === 'undefined') return;

        let lastScrollTop = 0;

        const handleScroll = () => {
            const currentScrollTop = window.pageYOffset;

            setIsScrollingUp(currentScrollTop < lastScrollTop);
            lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop;
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <HeaderWrapper isScrollingUp={isScrollingUp}>
            <div style={{width: showRegions ? '100vw' : 'auto', height: showRegions ? '100vh' : 'auto'}}
                 onClick={(e) => {
                     // Check if the clicked element is the parent itself
                     if (e.target === e.currentTarget) {
                         setShowRegions(false);
                     }
                 }}
            >
                <HeaderContainer>
                    <div
                        className={'container_header'}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px',
                            position: 'relative',
                        }}
                    >
                        <ForceSSRLink href={'/'} prefetch={false}>
                            <Image
                                priority
                                loading={'eager'}
                                width={1920}
                                height={1080}
                                src={headerData?.logo?.url}
                                alt={headerData?.logo?.alt ?? headerData?.logo?.name}
                                style={{
                                    width: '250px',
                                    height: 'auto',
                                }}
                            />
                        </ForceSSRLink>

                        <LinkWrapper isWorldTour={isWorldTour} >
                            <LinkItem
                                className={showRegions ? 'active' : null}
                                onClick={() => setShowRegions(!showRegions)}
                                onMouseEnter={() => setIsRegionsHover(true)}
                                onMouseLeave={() => setIsRegionsHover(false)}
                            >
                                DESTINATIONS
                                {showRegions ?
                                    <Image src={ArrowUpActiveIcon} alt={'^'}/>
                                    :
                                    isRegionsHover ? <Image src={ArrowDownActiveIcon} alt={'v'}/> :
                                        <Image src={ArrowDownIcon} alt={'v'}/>
                                }
                            </LinkItem>
                            <LinkItemLink prefetch={false} href={'/private-bookings'}>PRIVATE TOURS</LinkItemLink>
                            <LinkItemLink prefetch={false} href={'/buy-gift'}>GIFT CARDS</LinkItemLink>
                            <LinkItemLink prefetch={false} href={'https://www.secretfoodtours.com/blog'}>BLOG</LinkItemLink>
                            <LinkItemLink prefetch={false} href={'/read-about-us'}>ABOUT US</LinkItemLink>
                            {!!isWorldTour &&
                                <LinkItemLink prefetch={false} href={'/covid'} style={{color: '#00AF64'}}>HEALTH & SAFETY</LinkItemLink>}
                            <LinkItemLink prefetch={false} href={'https://www.secrettours.com/'} target={'_blank'} style={{
                                color: getColor('blue'),
                            }}>HISTORICAL TOURS</LinkItemLink>
                        </LinkWrapper>

                        <div
                            style={{
                                position: 'absolute',
                                right: '50px',
                                width: showSearchBar ? '75%' : '0px',
                                padding: '0px',
                                transition: 'width 0.5s ease, padding 0.5s ease',
                            }}
                        >
                            {showSearchBar && <SelectSearchLocation searchOptions={searchOptions}/>}
                        </div>

                        <Image
                            loading={'eager'}
                            onClick={() => setShowSearchBar(!showSearchBar)}
                            src={showSearchBar ? CloseIcon : SearchIcon}
                            alt={'Search'}
                        />
                    </div>
                </HeaderContainer>

                {/* REGIONS DROPDOWN */}
                <div
                    style={{
                        height: showRegions ? (!showRegionData ? 'fit-content' : 'calc(100vh - 200px)') : '0px',
                        maxHeight: showRegions ? (showRegionData ? 'fit-content' : 'fit-content') : '0px',
                        overflow: 'hidden',
                        background: getColor('grayBackground'),
                        padding: showRegions ? '40px' : '0px',
                        transition: 'max-height 0.5s ease, padding 0.5s ease',
                    }}
                >
                    <div
                        className={'container'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {regions?.map((item, index) => (
                            <div
                                onClick={() => setShowRegionData(showRegionData === item?.id ? null : item?.id)}
                                key={index}
                                style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '5px',
                                }}
                            >
                                <div style={{
                                    textTransform: 'uppercase',
                                    color: getColor(showRegionData === item?.id ? 'blue' : 'gold')
                                }}>{item?.name}</div>
                                <Image width={40} height={40} src={item?.navigationIcon?.url} alt={item?.name}/>
                            </div>
                        ))}
                    </div>

                    <ListContainer className={'container'}>
                        {
                            showRegions && showRegionData && <CountryList>
                                {regions.find(item => item?.id === showRegionData)?.countries?.map((item, index) => {
                                    return (
                                        <li key={index}>
                                            <CountryLink
                                                href={item?.slug ?? '#'}
                                                onClick={closeAll}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    marginBottom: '5px',
                                                }}>
                                                    <Image
                                                        src={item?.flag?.url ?? LocationPinIcon?.src}
                                                        alt={item?.label}
                                                        width={20}
                                                        height={20}
                                                        style={{
                                                            width: '20px',
                                                            height: 'auto',
                                                        }}
                                                    />
                                                    {item?.name}
                                                    {item?.isNew && <Image src={NewIcon} alt={'NEW'}/>}
                                                </div>
                                            </CountryLink>

                                            <ul>
                                                {item?.cities?.map((city, idx) => (
                                                    <CityLink
                                                        prefetch={false}
                                                        key={idx}
                                                        href={city?.slug ?? '#'}
                                                        onClick={closeAll}
                                                    >
                                                        <Image src={DotIcon} alt={city?.label}/>
                                                        {city.name}
                                                        {city?.isNew && <Image src={NewIcon} alt={'NEW'}/>}
                                                    </CityLink>
                                                ))}
                                            </ul>
                                        </li>
                                    );
                                })}
                            </CountryList>
                        }

                    </ListContainer>
                </div>
            </div>
        </HeaderWrapper>

)
    ;
}
